const LAST_REVIEWED = "2026-05-20";

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function hashString(value) {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
}

function pickDeterministic(items, seed, count = items.length) {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const orderedItems = [...items];
  const result = [];
  let cursor = hashString(seed) % orderedItems.length;

  while (result.length < Math.min(count, orderedItems.length)) {
    const candidate = orderedItems[cursor % orderedItems.length];

    if (!result.includes(candidate)) {
      result.push(candidate);
    }

    cursor += 1;
  }

  return result;
}

function prioritize(allItems, preferredItems = []) {
  return unique([...preferredItems, ...allItems]);
}

function buildNearbyAreas(towns, index) {
  return unique([
    towns[index - 2]?.name,
    towns[index - 1]?.name,
    towns[index + 1]?.name,
    towns[index + 2]?.name,
  ]).slice(0, 3);
}

export const locationServiceFamilies = [
  {
    kind: "service",
    slug: "health-safety",
    name: "Health & Safety Consultancy",
    shortName: "health and safety consultancy",
    title: "Health & Safety Consultancy",
    path: "/services/health-safety",
    description:
      "Practical health and safety support shaped around reviews, audits, assessments, systems and tailored service levels.",
    keywords: [
      "health and safety consultancy",
      "risk assessments",
      "site audits",
      "management systems",
      "compliance reviews",
    ],
    themes: [
      "audits and action plans",
      "management systems and policy support",
      "workplace and machinery assessments",
      "practical rollout across live operations",
    ],
    sectorSlugs: [
      "manufacturing",
      "logistics",
      "construction",
      "engineering",
      "retail",
    ],
  },
  {
    kind: "service",
    slug: "occupational-hygiene-consultancy",
    name: "Occupational Hygiene Consultancy",
    shortName: "occupational hygiene consultancy",
    title: "Occupational Hygiene Consultancy",
    path: "/services/occupational-hygiene-consultancy",
    description:
      "Consultancy-led support to identify, assess and control workplace health risks around air quality, noise, hazardous substances and exposure controls.",
    keywords: [
      "occupational hygiene",
      "air monitoring",
      "noise assessment",
      "dust exposure",
      "LEV testing",
    ],
    themes: [
      "air sampling and dust exposure",
      "LEV examination and testing",
      "noise monitoring and exposure review",
      "COSHH control improvement",
    ],
    sectorSlugs: ["manufacturing", "engineering", "logistics"],
  },
  {
    kind: "service",
    slug: "statutory-inspection-management",
    name: "Statutory Inspection Management",
    shortName: "statutory inspection management",
    title: "Statutory Inspection Management",
    path: "/services/statutory-inspection-management",
    description:
      "Advisory support to help organisations manage statutory engineering inspections, stay compliant and improve oversight under obligations including LOLER and PSSR.",
    keywords: [
      "statutory inspection management",
      "LOLER",
      "PSSR",
      "inspection programmes",
      "provider oversight",
    ],
    themes: [
      "inspection programme reviews",
      "provider and contract oversight",
      "maintenance and FM alignment",
      "defect close-out and governance",
    ],
    sectorSlugs: ["engineering", "manufacturing", "logistics", "retail"],
  },
  {
    kind: "service",
    slug: "outsourced-consultancy",
    name: "Outsourced Health & Safety Consultancy",
    shortName: "outsourced health and safety consultancy",
    title: "Outsourced Health & Safety Consultancy",
    path: "/services/outsourced-consultancy",
    description:
      "Flexible outsourced health and safety consultancy for organisations that need trusted oversight, strategic guidance and a cost-effective alternative to an in-house team.",
    keywords: [
      "outsourced health and safety",
      "competent person support",
      "retained consultancy",
      "health and safety adviser",
      "multi-site compliance support",
    ],
    themes: [
      "competent person oversight",
      "retained consultancy and governance",
      "annual reviews and compliance planning",
      "remote advice backed by site visits",
    ],
    sectorSlugs: [
      "manufacturing",
      "logistics",
      "construction",
      "engineering",
      "retail",
    ],
  },
  {
    kind: "service",
    slug: "training",
    name: "Health & Safety Training",
    shortName: "health and safety training",
    title: "Health & Safety Training",
    path: "/services/training",
    description:
      "Consultant-led health and safety training aligned to real risk, legal duties and safer day-to-day decision-making in the workplace.",
    keywords: [
      "health and safety training",
      "consultant-led training",
      "onsite training",
      "management awareness",
      "risk-based training",
    ],
    themes: [
      "role-specific awareness programmes",
      "onsite and offsite delivery",
      "management briefing sessions",
      "training aligned to changing risk and procedures",
    ],
    sectorSlugs: [
      "manufacturing",
      "logistics",
      "construction",
      "engineering",
      "retail",
    ],
  },
];

export const locationSectorFamilies = [
  {
    kind: "sector",
    slug: "manufacturing",
    name: "Manufacturing Health & Safety Consultancy",
    shortName: "manufacturing health and safety support",
    title: "Manufacturing",
    path: "/sectors/manufacturing",
    description:
      "Support for manufacturing businesses managing machinery, process risk, exposure and compliance across busy production sites.",
    keywords: [
      "manufacturing safety",
      "machinery risk",
      "DSEAR",
      "COSHH",
      "production site audits",
    ],
    serviceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "statutory-inspection-management",
      "outsourced-consultancy",
      "training",
    ],
  },
  {
    kind: "sector",
    slug: "logistics",
    name: "Logistics Health & Safety Consultancy",
    shortName: "logistics health and safety support",
    title: "Logistics",
    path: "/sectors/logistics",
    description:
      "Support for warehousing, distribution and transport operations where vehicle movement, contractor activity and operational pace create risk.",
    keywords: [
      "logistics safety",
      "warehouse safety",
      "workplace transport",
      "distribution audits",
      "multi-site logistics compliance",
    ],
    serviceSlugs: [
      "health-safety",
      "statutory-inspection-management",
      "outsourced-consultancy",
      "training",
      "occupational-hygiene-consultancy",
    ],
  },
  {
    kind: "sector",
    slug: "construction",
    name: "Construction Health & Safety Consultancy",
    shortName: "construction health and safety support",
    title: "Construction",
    path: "/sectors/construction",
    description:
      "Clear guidance for site safety, contractor control and changing project risk across live construction environments.",
    keywords: [
      "construction safety",
      "contractor control",
      "RAMS",
      "site audits",
      "project safety support",
    ],
    serviceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "statutory-inspection-management",
    ],
  },
  {
    kind: "sector",
    slug: "engineering",
    name: "Engineering Health & Safety Consultancy",
    shortName: "engineering health and safety support",
    title: "Engineering",
    path: "/sectors/engineering",
    description:
      "Expert input for plant, equipment and technical operations where compliance needs to keep pace with output and maintenance demands.",
    keywords: [
      "engineering safety",
      "plant safety",
      "technical compliance",
      "inspection management",
      "maintenance risk",
    ],
    serviceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "statutory-inspection-management",
      "training",
      "outsourced-consultancy",
    ],
  },
  {
    kind: "sector",
    slug: "retail",
    name: "Retail Health & Safety Consultancy",
    shortName: "retail health and safety support",
    title: "Retail",
    path: "/sectors/retail",
    description:
      "Practical health and safety support for customer-facing teams, premises and back-of-house operations in retail settings.",
    keywords: [
      "retail safety",
      "customer-facing premises",
      "retail estates",
      "slip trip risk",
      "store compliance",
    ],
    serviceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "statutory-inspection-management",
    ],
  },
];

const ALL_SERVICE_SLUGS = locationServiceFamilies.map((family) => family.slug);
const ALL_SECTOR_SLUGS = locationSectorFamilies.map((family) => family.slug);

const CORE_LOCATION_REGIONS = [
  {
    slug: "bedfordshire",
    name: "Bedfordshire",
    focus:
      "distribution, engineering, office and light industrial operations around Bedford, Luton and Dunstable.",
    coverageNote:
      "SML supports Bedfordshire organisations from its Daventry base with practical audits, assessments, training and retained consultancy without implying a staffed local office.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "occupational-hygiene-consultancy",
    ],
    prioritySectorSlugs: [
      "logistics",
      "manufacturing",
      "engineering",
      "retail",
    ],
    localRiskThemes: [
      "warehouse traffic and loading operations",
      "contractor control during site change and maintenance",
      "consistent standards across multi-site estates",
      "noise, dust and exposure review where processes intensify",
      "practical management training for fast-moving teams",
    ],
    towns: [
      { slug: "bedford", name: "Bedford" },
      { slug: "luton", name: "Luton" },
      { slug: "dunstable", name: "Dunstable" },
      { slug: "leighton-buzzard", name: "Leighton Buzzard" },
    ],
  },
  {
    slug: "buckinghamshire",
    name: "Buckinghamshire",
    focus:
      "mixed operational estates across Milton Keynes, Aylesbury, High Wycombe and surrounding commercial centres.",
    coverageNote:
      "Coverage across Buckinghamshire is built around regional travel, project-based support and retained consultancy for businesses that need day-to-day control as well as strategic oversight.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "statutory-inspection-management",
    ],
    prioritySectorSlugs: [
      "logistics",
      "engineering",
      "manufacturing",
      "retail",
    ],
    localRiskThemes: [
      "multi-site governance and reporting",
      "contractor management around estates and projects",
      "inspection oversight for plant and lifting assets",
      "training rollouts for supervisors and managers",
      "workplace transport and yard segregation",
    ],
    towns: [
      { slug: "olney", name: "Olney" },
      { slug: "milton-keynes", name: "Milton Keynes" },
      { slug: "aylesbury", name: "Aylesbury" },
      { slug: "high-wycombe", name: "High Wycombe" },
      { slug: "buckingham", name: "Buckingham" },
      { slug: "newport-pagnell", name: "Newport Pagnell" },
      { slug: "amersham", name: "Amersham" },
      { slug: "beaconsfield", name: "Beaconsfield" },
    ],
  },
  {
    slug: "northamptonshire",
    name: "Northamptonshire",
    focus:
      "manufacturing, logistics, engineering and multi-site organisations across Daventry, Northampton and the wider county.",
    coverageNote:
      "Northamptonshire is central to SML's coverage story, giving nearby access for audits, retained support, statutory inspection oversight, occupational hygiene and training delivery.",
    priorityServiceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "statutory-inspection-management",
      "outsourced-consultancy",
      "training",
    ],
    prioritySectorSlugs: [
      "manufacturing",
      "logistics",
      "engineering",
      "construction",
    ],
    localRiskThemes: [
      "machinery, maintenance and engineering change",
      "warehouse traffic, loading and contractor movement",
      "noise, dust and exposure monitoring",
      "inspection governance for asset-heavy sites",
      "leadership consistency across multiple locations",
    ],
    towns: [
      { slug: "northampton", name: "Northampton" },
      { slug: "kettering", name: "Kettering" },
      { slug: "wellingborough", name: "Wellingborough" },
      { slug: "corby", name: "Corby" },
      { slug: "daventry", name: "Daventry" },
      { slug: "towcester", name: "Towcester" },
      { slug: "rushden", name: "Rushden" },
      { slug: "brackley", name: "Brackley" },
      { slug: "market-harborough", name: "Market Harborough" },
      { slug: "thrapston", name: "Thrapston" },
    ],
  },
  {
    slug: "leicestershire",
    name: "Leicestershire",
    focus:
      "manufacturing, engineering and logistics businesses that need clear operational control and stronger consistency between sites.",
    coverageNote:
      "SML's Leicestershire coverage is aimed at businesses balancing operational pace with practical compliance, technical exposure risk and structured management oversight.",
    priorityServiceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "training",
      "statutory-inspection-management",
    ],
    prioritySectorSlugs: [
      "manufacturing",
      "engineering",
      "logistics",
      "retail",
    ],
    localRiskThemes: [
      "process safety and machinery guarding",
      "noise, dust and substance exposure",
      "warehouse and dispatch activity",
      "management systems that hold up across sites",
      "inspection close-out and action tracking",
    ],
    towns: [
      { slug: "leicester", name: "Leicester" },
      { slug: "loughborough", name: "Loughborough" },
      { slug: "hinckley", name: "Hinckley" },
      { slug: "melton-mowbray", name: "Melton Mowbray" },
    ],
  },
  {
    slug: "hertfordshire",
    name: "Hertfordshire",
    focus:
      "commercial estates, warehousing, engineering and customer-facing environments that need joined-up oversight.",
    coverageNote:
      "Hertfordshire support is structured around regional response, focused reviews and longer-term retained programmes where multiple sites or changing operations need consistent guidance.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "statutory-inspection-management",
    ],
    prioritySectorSlugs: ["logistics", "engineering", "retail", "construction"],
    localRiskThemes: [
      "contractor and project coordination",
      "workplace transport near mixed-use estates",
      "training for supervisors and managers",
      "inspection governance across distributed assets",
      "consistency between local delivery and central standards",
    ],
    towns: [
      { slug: "st-albans", name: "St Albans" },
      { slug: "watford", name: "Watford" },
      { slug: "stevenage", name: "Stevenage" },
      { slug: "hemel-hempstead", name: "Hemel Hempstead" },
    ],
  },
  {
    slug: "oxfordshire",
    name: "Oxfordshire",
    focus:
      "technical, engineering, logistics and operational workplaces that need credible safety leadership as activity scales.",
    coverageNote:
      "Oxfordshire coverage blends practical site support with strategic advice for businesses that want stronger local control without adding unnecessary process overhead.",
    priorityServiceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "outsourced-consultancy",
      "training",
    ],
    prioritySectorSlugs: [
      "engineering",
      "manufacturing",
      "logistics",
      "construction",
    ],
    localRiskThemes: [
      "technical risk assessment and specialist exposure review",
      "contractor control on complex operational sites",
      "training tied to procedure and system change",
      "multi-site rollout consistency",
      "safe maintenance and inspection planning",
    ],
    towns: [
      { slug: "oxford", name: "Oxford" },
      { slug: "banbury", name: "Banbury" },
      { slug: "bicester", name: "Bicester" },
      { slug: "witney", name: "Witney" },
      { slug: "abingdon", name: "Abingdon" },
    ],
  },
  {
    slug: "warwickshire",
    name: "Warwickshire",
    focus:
      "manufacturing, engineering, warehousing and operational estates across Coventry-adjacent and Midlands locations.",
    coverageNote:
      "SML supports Warwickshire businesses that need pragmatic leadership, better risk visibility and scalable support across busy operational footprints.",
    priorityServiceSlugs: [
      "health-safety",
      "statutory-inspection-management",
      "outsourced-consultancy",
      "training",
    ],
    prioritySectorSlugs: [
      "engineering",
      "manufacturing",
      "logistics",
      "construction",
    ],
    localRiskThemes: [
      "asset-heavy operations and inspection control",
      "machinery, maintenance and contractor interfaces",
      "warehouse and yard movement",
      "management training and operational accountability",
      "joined-up action tracking across sites",
    ],
    towns: [
      { slug: "warwick", name: "Warwick" },
      { slug: "stratford-upon-avon", name: "Stratford-upon-Avon" },
      { slug: "rugby", name: "Rugby" },
      { slug: "nuneaton", name: "Nuneaton" },
      { slug: "leamington-spa", name: "Leamington Spa" },
      { slug: "kenilworth", name: "Kenilworth" },
    ],
  },
  {
    slug: "lincolnshire",
    name: "Lincolnshire",
    focus:
      "operational sites with broad geographical spread, practical supervision challenges and a need for consistent regional coverage.",
    coverageNote:
      "Lincolnshire support is shaped around travel-efficient reviews, clear follow-up planning and practical leadership support across dispersed operations.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "occupational-hygiene-consultancy",
    ],
    prioritySectorSlugs: [
      "manufacturing",
      "logistics",
      "engineering",
      "retail",
    ],
    localRiskThemes: [
      "regional consistency across dispersed workplaces",
      "contractor control and planned works",
      "training and supervision for varied local teams",
      "exposure review where processes create dust or noise",
      "site audits that translate into realistic follow-up",
    ],
    towns: [
      { slug: "lincoln", name: "Lincoln" },
      { slug: "boston", name: "Boston" },
      { slug: "grantham", name: "Grantham" },
      { slug: "stamford", name: "Stamford" },
      { slug: "oundle", name: "Oundle" },
    ],
  },
  {
    slug: "essex",
    name: "Essex",
    focus:
      "large estates, mixed operational risk and customer-facing environments that need stronger consistency and leadership oversight.",
    coverageNote:
      "SML covers Essex with a programme-led model that suits businesses needing regional support across audits, training, retained advice and operational reviews.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "statutory-inspection-management",
    ],
    prioritySectorSlugs: ["retail", "logistics", "engineering", "construction"],
    localRiskThemes: [
      "consistency across estates and multiple trading locations",
      "contractor and facilities coordination",
      "vehicle movement and loading operations",
      "management accountability and refresher training",
      "asset oversight and inspection performance",
    ],
    towns: [
      { slug: "chelmsford", name: "Chelmsford" },
      { slug: "colchester", name: "Colchester" },
      { slug: "southend-on-sea", name: "Southend-on-Sea" },
      { slug: "basildon", name: "Basildon" },
      { slug: "braintree", name: "Braintree" },
    ],
  },
  {
    slug: "gloucestershire",
    name: "Gloucestershire",
    focus:
      "manufacturing, engineering, warehousing and operational sites that need credible, scalable health and safety support.",
    coverageNote:
      "Gloucestershire support is aimed at businesses that need pragmatic audits, exposure control, inspection oversight and training that works in live environments.",
    priorityServiceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "training",
      "outsourced-consultancy",
    ],
    prioritySectorSlugs: [
      "manufacturing",
      "engineering",
      "logistics",
      "construction",
    ],
    localRiskThemes: [
      "machinery, maintenance and process risk",
      "noise, dust and control effectiveness",
      "contractor management around upgrades and shutdowns",
      "training for supervisors and local decision-makers",
      "action planning that can be delivered across multiple sites",
    ],
    towns: [
      { slug: "gloucester", name: "Gloucester" },
      { slug: "cheltenham", name: "Cheltenham" },
      { slug: "stroud", name: "Stroud" },
      { slug: "tewkesbury", name: "Tewkesbury" },
      { slug: "cirencester", name: "Cirencester" },
      { slug: "lydney", name: "Lydney" },
    ],
  },
  {
    slug: "west-midlands",
    name: "West Midlands",
    focus:
      "large, mixed-use and contractor-heavy operations across major urban commercial centres.",
    coverageNote:
      "West Midlands coverage is designed for organisations that need robust systems, clear leadership expectations and practical support across busy, high-change sites.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "statutory-inspection-management",
      "training",
    ],
    prioritySectorSlugs: ["engineering", "construction", "logistics", "retail"],
    localRiskThemes: [
      "contractor-heavy operations and permit control",
      "asset oversight across complex estates",
      "workplace transport and public-facing interfaces",
      "management systems that need stronger local accountability",
      "training programmes for diverse teams and supervisors",
    ],
    towns: [
      { slug: "birmingham", name: "Birmingham" },
      { slug: "wolverhampton", name: "Wolverhampton" },
      { slug: "coventry", name: "Coventry" },
      { slug: "dudley", name: "Dudley" },
      { slug: "solihull", name: "Solihull" },
    ],
  },
  {
    slug: "staffordshire",
    name: "Staffordshire",
    focus:
      "industrial, engineering and logistics operations that need clearer control over risk, contractors and asset performance.",
    coverageNote:
      "Staffordshire support is built for organisations that need a practical balance between compliance confidence, site-level action and scalable training.",
    priorityServiceSlugs: [
      "health-safety",
      "statutory-inspection-management",
      "occupational-hygiene-consultancy",
      "training",
    ],
    prioritySectorSlugs: [
      "engineering",
      "manufacturing",
      "logistics",
      "construction",
    ],
    localRiskThemes: [
      "inspection and maintenance governance",
      "exposure review in industrial processes",
      "contractor coordination and safe systems",
      "workplace transport and dispatch risk",
      "leadership follow-through on corrective actions",
    ],
    towns: [
      { slug: "stoke-on-trent", name: "Stoke-on-Trent" },
      { slug: "stafford", name: "Stafford" },
      { slug: "lichfield", name: "Lichfield" },
      { slug: "burton-upon-trent", name: "Burton upon Trent" },
      { slug: "tamworth", name: "Tamworth" },
    ],
  },
  {
    slug: "derbyshire",
    name: "Derbyshire",
    focus:
      "manufacturing, engineering and warehousing businesses that need strong operational control and consistent local decision-making.",
    coverageNote:
      "Derbyshire coverage combines audits, retained advice, exposure review, inspection management and training to support busy technical and production environments.",
    priorityServiceSlugs: [
      "health-safety",
      "occupational-hygiene-consultancy",
      "statutory-inspection-management",
      "training",
    ],
    prioritySectorSlugs: ["manufacturing", "engineering", "logistics"],
    localRiskThemes: [
      "machinery and process safety",
      "noise, dust and hazardous substance control",
      "maintenance and engineering change",
      "warehouse movement and loading activities",
      "training tied to practical supervision",
    ],
    towns: [
      { slug: "derby", name: "Derby" },
      { slug: "chesterfield", name: "Chesterfield" },
      { slug: "buxton", name: "Buxton" },
      { slug: "long-eaton", name: "Long Eaton" },
      { slug: "ilkeston", name: "Ilkeston" },
    ],
  },
  {
    slug: "cambridgeshire",
    name: "Cambridgeshire",
    focus:
      "distributed operational businesses that need clear governance, structured rollout and practical site-level follow-through.",
    coverageNote:
      "Cambridgeshire support is delivered through region-aware planning, site audits, retained consultancy and training programmes that can scale across several locations.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "occupational-hygiene-consultancy",
    ],
    prioritySectorSlugs: [
      "engineering",
      "logistics",
      "manufacturing",
      "retail",
    ],
    localRiskThemes: [
      "multi-site consistency and reporting",
      "contractor and facilities coordination",
      "training aligned to changing local operations",
      "exposure review where technical work increases health risk",
      "practical audits that translate into accountable actions",
    ],
    towns: [
      { slug: "cambridge", name: "Cambridge" },
      { slug: "peterborough", name: "Peterborough" },
      { slug: "ely", name: "Ely" },
      { slug: "huntingdon", name: "Huntingdon" },
      { slug: "st-neots", name: "St Neots" },
    ],
  },
  {
    slug: "suffolk",
    name: "Suffolk",
    focus:
      "regional operational coverage for organisations that need a dependable partner across dispersed sites and changing risk profiles.",
    coverageNote:
      "Suffolk coverage is positioned around realistic travel, practical local support and central coordination for businesses that need clarity across more than one site.",
    priorityServiceSlugs: [
      "health-safety",
      "outsourced-consultancy",
      "training",
      "occupational-hygiene-consultancy",
    ],
    prioritySectorSlugs: [
      "logistics",
      "manufacturing",
      "retail",
      "engineering",
    ],
    localRiskThemes: [
      "regional rollout of consistent standards",
      "vehicle movement and contractor interfaces",
      "training and refresher planning for distributed teams",
      "dust, noise and exposure review where processes require it",
      "practical follow-up after audits and reviews",
    ],
    towns: [
      { slug: "ipswich", name: "Ipswich" },
      { slug: "bury-st-edmunds", name: "Bury St Edmunds" },
      { slug: "lowestoft", name: "Lowestoft" },
      { slug: "haverhill", name: "Haverhill" },
      { slug: "newmarket", name: "Newmarket" },
    ],
  },
];

function enrichTown(region, town, index, towns) {
  const seed = `${region.slug}:${town.slug}`;

  return {
    ...town,
    enabled: true,
    tier: "benchmark",
    lastReviewed: LAST_REVIEWED,
    regionSlug: region.slug,
    regionName: region.name,
    serviceFit: prioritize(ALL_SERVICE_SLUGS, region.priorityServiceSlugs),
    sectorFit: prioritize(ALL_SECTOR_SLUGS, region.prioritySectorSlugs),
    featuredServiceSlugs: pickDeterministic(
      prioritize(ALL_SERVICE_SLUGS, region.priorityServiceSlugs),
      `${seed}:service`,
      3,
    ),
    featuredSectorSlugs: pickDeterministic(
      prioritize(ALL_SECTOR_SLUGS, region.prioritySectorSlugs),
      `${seed}:sector`,
      3,
    ),
    localRiskThemes: pickDeterministic(
      region.localRiskThemes,
      `${seed}:risk`,
      4,
    ),
    nearbyAreas: buildNearbyAreas(towns, index),
    coverageNote: `SML supports businesses in ${town.name} with practical site visits, remote advice and follow-through shaped around local operations and the wider ${region.name} area.`,
    proofPoints: [
      {
        title: "Clear action after reviews",
        description: `Support for ${town.name} businesses that need clear priorities after audits, assessments or operational change.`,
      },
      {
        title: "Consistency across sites and teams",
        description: `Support that keeps local standards in step with wider expectations across ${region.name}.`,
      },
      {
        title: "Consultancy and training that scales",
        description: `Consultancy, training and technical input that can grow with site complexity and change.`,
      },
    ],
  };
}

function enrichRegion(region) {
  const towns = region.towns.map((town, index, regionTowns) =>
    enrichTown(region, town, index, regionTowns),
  );

  return {
    ...region,
    enabled: true,
    tier: "benchmark",
    lastReviewed: LAST_REVIEWED,
    townCount: towns.length,
    serviceFit: prioritize(ALL_SERVICE_SLUGS, region.priorityServiceSlugs),
    sectorFit: prioritize(ALL_SECTOR_SLUGS, region.prioritySectorSlugs),
    featuredServiceSlugs: pickDeterministic(
      prioritize(ALL_SERVICE_SLUGS, region.priorityServiceSlugs),
      `${region.slug}:service`,
      3,
    ),
    featuredSectorSlugs: pickDeterministic(
      prioritize(ALL_SECTOR_SLUGS, region.prioritySectorSlugs),
      `${region.slug}:sector`,
      3,
    ),
    proofPoints: [
      {
        title: "Regional operating picture",
        description: `Regional support across ${region.focus}`,
      },
      {
        title: "Practical delivery mix",
        description: `Programmes that combine audits, advice, training and follow-through where local teams need practical momentum.`,
      },
      {
        title: "Leadership visibility",
        description:
          "Support for organisations balancing day-to-day risk with central oversight expectations.",
      },
    ],
    towns,
  };
}

export const locationRegions = CORE_LOCATION_REGIONS.map(enrichRegion);

export const locationRegionMap = Object.fromEntries(
  locationRegions.map((region) => [region.slug, region]),
);

export const locationServiceMap = Object.fromEntries(
  locationServiceFamilies.map((family) => [family.slug, family]),
);

export const locationSectorMap = Object.fromEntries(
  locationSectorFamilies.map((family) => [family.slug, family]),
);

export function getLocationRegions() {
  return locationRegions;
}

export function getLocationRegion(regionSlug) {
  return locationRegionMap[regionSlug] ?? null;
}

export function getLocationTown(regionSlug, townSlug) {
  const region = getLocationRegion(regionSlug);

  if (!region) {
    return null;
  }

  return region.towns.find((town) => town.slug === townSlug) ?? null;
}

export function getLocationServiceFamily(serviceSlug) {
  return locationServiceMap[serviceSlug] ?? null;
}

export function getLocationSectorFamily(sectorSlug) {
  return locationSectorMap[sectorSlug] ?? null;
}

export function getLocationFamily(kind, slug) {
  if (kind === "sector") {
    return getLocationSectorFamily(slug);
  }

  return getLocationServiceFamily(slug);
}

export function getLocationsHubPath() {
  return "/locations";
}

export function getLocationRegionPath(regionSlug) {
  return `/locations/${regionSlug}`;
}

export function getLocationTownPath(regionSlug, townSlug) {
  return `${getLocationRegionPath(regionSlug)}/${townSlug}`;
}

export function getLocationFamilyRegionPath(family) {
  return (regionSlug) => `${family.path}/${regionSlug}`;
}

export function getLocationFamilyTownPath(family) {
  return (regionSlug, townSlug) => `${family.path}/${regionSlug}/${townSlug}`;
}

export function getAllLocationRegionParams() {
  return locationRegions.map((region) => ({ region: region.slug }));
}

export function getAllLocationTownParams() {
  return locationRegions.flatMap((region) =>
    region.towns.map((town) => ({ region: region.slug, town: town.slug })),
  );
}

export function getAllLocationFamilyRegionParams() {
  return getAllLocationRegionParams();
}

export function getAllLocationFamilyTownParams() {
  return locationRegions.flatMap((region) =>
    region.towns.map((town) => ({ region: region.slug, town: town.slug })),
  );
}

export function getLocationRouteCounts() {
  const regionCount = locationRegions.length;
  const townCount = locationRegions.reduce(
    (total, region) => total + region.towns.length,
    0,
  );
  const hubRoutes = 1 + regionCount + townCount;
  const serviceRoutes =
    locationServiceFamilies.length * (regionCount + townCount);
  const sectorRoutes =
    locationSectorFamilies.length * (regionCount + townCount);

  return {
    regionCount,
    townCount,
    hubRoutes,
    serviceRoutes,
    sectorRoutes,
    totalProgrammaticRoutes: hubRoutes + serviceRoutes + sectorRoutes,
  };
}

export function getFeaturedLocationLinks(limit = 6) {
  return locationRegions.slice(0, limit).map((region) => ({
    href: getLocationRegionPath(region.slug),
    label: region.name,
    description: `Support across ${region.name} and ${region.townCount} towns.`,
  }));
}

export function getFamilyFeaturedRegions(family, limit = 6) {
  return locationRegions.slice(0, limit).map((region) => ({
    href: getLocationFamilyRegionPath(family)(region.slug),
    label: region.name,
    description: `${family.title} support across ${region.name} and surrounding towns.`,
  }));
}

export function getFamilyFeaturedTowns(family, limit = 6) {
  return locationRegions
    .flatMap((region) =>
      region.towns.slice(0, 2).map((town) => ({
        href: getLocationFamilyTownPath(family)(region.slug, town.slug),
        label: `${town.name}, ${region.name}`,
        description: `${family.title} support connected to ${town.name} and nearby areas including ${town.nearbyAreas.join(", ")}.`,
      })),
    )
    .slice(0, limit);
}

export function getRegionSummaryLinks(region, familySlugs, resolver) {
  return familySlugs.map((slug) => {
    const family = resolver(slug);

    return {
      href: family.path,
      label: family.title,
      description: family.description,
    };
  });
}

export const FULL_BENCHMARK_ROLLOUT_NOTE =
  "SML's location coverage spans 15 regions and 82 towns, with related service and sector support for each area.";
